/**
 * Competition Prep Screen — meet planning, attempt selection, weight cut & checklist.
 * Accessible from Settings (shown for users who have logged the big 3).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Trophy, Calendar, Scale, ClipboardList, Timer } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';
import {
  recommendAttempts,
  projectedTotal,
  daysUntilMeet,
  weightCutStatus,
  MEET_DAY_CHECKLIST,
  saveMeetSetup,
  type AttemptRecommendation,
} from '@/lib/competitionPrep';
import { calculateE1RM } from '@/lib/lifts/canonical';
import { TYPO } from '@/constants/typography';

const WEIGHT_CLASSES = [59, 66, 74, 83, 93, 105, 120, 999]; // 999 = 120kg+
const FEDERATIONS = ['USAPL', 'USPA', 'IPF', 'WRPF'];
const CHECKLIST_KEY = 'comp_prep_checklist_v1';
const MEET_SETUP_KEY = 'comp_prep_meet_setup_v1';

type AttemptField = 'opener' | 'second' | 'third';
type LiftKey = 'squat' | 'bench' | 'deadlift';

interface AttemptState {
  squat: { opener: string; second: string; third: string };
  bench: { opener: string; second: string; third: string };
  deadlift: { opener: string; second: string; third: string };
}

export default function CompetitionPrepScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { user, profile } = useAuth();

  // ── Meet Setup ──
  const [meetName, setMeetName] = useState('');
  const [meetDate, setMeetDate] = useState('');
  const [dateStatus, setDateStatus] = useState<'idle' | 'valid' | 'invalid' | 'past'>('idle');
  const [weightClass, setWeightClass] = useState<number>(83);
  const [federation, setFederation] = useState('USAPL');
  const [saving, setSaving] = useState(false);

  // ── Best lifts (fetched from DB) ──
  const [bestLifts, setBestLifts] = useState<{ squat: number; bench: number; deadlift: number } | null>(null);
  const [liftsLoading, setLiftsLoading] = useState(true);

  // ── Attempts ──
  const [attempts, setAttempts] = useState<AttemptState>({
    squat: { opener: '', second: '', third: '' },
    bench: { opener: '', second: '', third: '' },
    deadlift: { opener: '', second: '', third: '' },
  });

  // ── Checklist ──
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  // Fetch best lifts
  const fetchBestLifts = useCallback(async () => {
    if (!user?.id) return;
    setLiftsLoading(true);
    try {
      // Query workouts, scan for best squat/bench/deadlift e1RM
      const { data } = await supabase
        .from('workouts')
        .select('payload, performed_at')
        .eq('user_id', user.id)
        .order('performed_at', { ascending: false })
        .limit(500);

      const bests = { squat: 0, bench: 0, deadlift: 0 };

      if (data) {
        for (const row of data) {
          const exercises = row.payload?.exercises ?? [];
          for (const ex of exercises) {
            const name = (ex.name ?? '').toLowerCase();
            const isSquat = name.includes('squat');
            const isBench = name.includes('bench');
            const isDeadlift = name.includes('deadlift');

            for (const set of ex.sets ?? []) {
              const e1rm = calculateE1RM(set.weight ?? 0, set.reps ?? 0);
              if (isSquat && e1rm > bests.squat) bests.squat = e1rm;
              if (isBench && e1rm > bests.bench) bests.bench = e1rm;
              if (isDeadlift && e1rm > bests.deadlift) bests.deadlift = e1rm;
            }
          }
        }
      }

      setBestLifts(bests);

      // Auto-populate attempt suggestions
      if (bests.squat > 0 || bests.bench > 0 || bests.deadlift > 0) {
        const recs = recommendAttempts(bests.bench, bests.squat, bests.deadlift);
        const newAttempts: AttemptState = {
          squat: { opener: '', second: '', third: '' },
          bench: { opener: '', second: '', third: '' },
          deadlift: { opener: '', second: '', third: '' },
        };
        for (const rec of recs) {
          const key = rec.lift.toLowerCase().replace(' press', '') as LiftKey;
          if (key === 'squat' || key === 'bench' || key === 'deadlift') {
            newAttempts[key] = {
              opener: String(rec.opener),
              second: String(rec.second),
              third: String(rec.third),
            };
          }
        }
        // Only pre-fill if user hasn't touched them yet
        setAttempts((prev) => {
          const allEmpty = Object.values(prev).every((l) =>
            Object.values(l).every((v) => v === '')
          );
          return allEmpty ? newAttempts : prev;
        });
      }
    } finally {
      setLiftsLoading(false);
    }
  }, [user?.id]);

  // Load persisted checklist + meet setup
  const loadPersisted = useCallback(async () => {
    try {
      const [checklistJson, meetJson] = await Promise.all([
        AsyncStorage.getItem(CHECKLIST_KEY),
        AsyncStorage.getItem(MEET_SETUP_KEY),
      ]);
      if (checklistJson) setCheckedItems(JSON.parse(checklistJson));
      if (meetJson) {
        const m = JSON.parse(meetJson);
        if (m.meetName) setMeetName(m.meetName);
        if (m.meetDate) setMeetDate(m.meetDate);
        if (m.weightClass) setWeightClass(m.weightClass);
        if (m.federation) setFederation(m.federation);
        if (m.attempts) setAttempts(m.attempts);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadPersisted();
    fetchBestLifts();
  }, [loadPersisted, fetchBestLifts]);

  const persistMeetSetup = async (updates: Partial<{
    meetName: string; meetDate: string; weightClass: number;
    federation: string; attempts: AttemptState;
  }>) => {
    try {
      const current = await AsyncStorage.getItem(MEET_SETUP_KEY);
      const existing = current ? JSON.parse(current) : {};
      await AsyncStorage.setItem(MEET_SETUP_KEY, JSON.stringify({ ...existing, ...updates }));
    } catch {}
  };

  const toggleCheckItem = async (id: string) => {
    const next = { ...checkedItems, [id]: !checkedItems[id] };
    setCheckedItems(next);
    try {
      await AsyncStorage.setItem(CHECKLIST_KEY, JSON.stringify(next));
    } catch {}
  };

  const handleSave = async () => {
    if (!user?.id || !meetName.trim() || !meetDate.trim()) {
      const msg = 'Please enter a meet name and date before saving.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Missing Info', msg);
      return;
    }
    setSaving(true);
    try {
      await saveMeetSetup({
        user_id: user.id,
        meet_name: meetName.trim(),
        meet_date: meetDate,
        weight_class_kg: weightClass === 999 ? 999 : weightClass,
        federation,
        lifts: {
          squat: {
            opener: Number(attempts.squat.opener) || 0,
            second: Number(attempts.squat.second) || 0,
            third: Number(attempts.squat.third) || 0,
          },
          bench: {
            opener: Number(attempts.bench.opener) || 0,
            second: Number(attempts.bench.second) || 0,
            third: Number(attempts.bench.third) || 0,
          },
          deadlift: {
            opener: Number(attempts.deadlift.opener) || 0,
            second: Number(attempts.deadlift.second) || 0,
            third: Number(attempts.deadlift.third) || 0,
          },
        },
        status: 'prep',
      });
      await persistMeetSetup({ meetName, meetDate, weightClass, federation, attempts });
      const msg = 'Meet saved!';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Saved', msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Derived values ──
  const days = meetDate ? daysUntilMeet(meetDate) : null;

  const total = projectedTotal(
    Number(attempts.squat.third) || 0,
    Number(attempts.bench.third) || 0,
    Number(attempts.deadlift.third) || 0,
  );

  const cutStatus =
    profile?.bodyweight && weightClass
      ? weightCutStatus(profile.bodyweight, weightClass === 999 ? 999 : weightClass)
      : null;

  const categorized: Record<string, typeof MEET_DAY_CHECKLIST> = {};
  for (const item of MEET_DAY_CHECKLIST) {
    if (!categorized[item.category]) categorized[item.category] = [];
    categorized[item.category].push(item);
  }

  const weightClassLabel = (kg: number) => (kg === 999 ? '120kg+' : `${kg}kg`);

  const updateAttempt = (lift: LiftKey, field: AttemptField, value: string) => {
    const next = {
      ...attempts,
      [lift]: { ...attempts[lift], [field]: value },
    };
    setAttempts(next);
    persistMeetSetup({ attempts: next });
  };

  return (
    <SafeAreaView style={{ backgroundColor: c.background, flex: 1 }} edges={['top']}>
      <View style={{ flex: 1 }}>
        {/* Header */}
        <LinearGradient
          colors={[c.background, c.surfaceVariant]}
          style={[styles.header, { borderBottomColor: c.border }]}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => router.back()} hitSlop={12} style={{ width: 28 }}>
              <ArrowLeft size={24} color={c.text} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: c.accent }]}>COMPETITION PREP</Text>
            <View style={{ width: 28 }} />
          </View>
        </LinearGradient>

        <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">

          {/* ── Section 1: Meet Setup ── */}
          <View style={[styles.section, { backgroundColor: c.surface }]}>
            <View style={styles.sectionHeaderRow}>
              <Trophy size={16} color={c.accent} />
              <Text style={[styles.sectionTitle, { color: c.text }]}>MEET SETUP</Text>
            </View>

            <View style={styles.fieldGroup}>
              <Text style={[styles.fieldLabel, { color: c.textSecondary }]}>Meet Name</Text>
              <TextInput
                style={[styles.input, { backgroundColor: c.surfaceVariant, borderColor: c.border, color: c.text }]}
                placeholder="e.g. USAPL State Championships"
                placeholderTextColor={c.textTertiary}
                value={meetName}
                onChangeText={(v) => { setMeetName(v); persistMeetSetup({ meetName: v }); }}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={[styles.fieldLabel, { color: c.textSecondary }]}>Meet Date (YYYY-MM-DD)</Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: c.surfaceVariant,
                    color: c.text,
                    borderColor:
                      dateStatus === 'valid' ? '#22c55e' :
                      dateStatus === 'invalid' || dateStatus === 'past' ? '#ef4444' :
                      c.border,
                  },
                ]}
                placeholder="e.g. 2026-09-20"
                placeholderTextColor={c.textTertiary}
                value={meetDate}
                onChangeText={(v) => {
                  setMeetDate(v);
                  persistMeetSetup({ meetDate: v });
                  if (!v) { setDateStatus('idle'); return; }
                  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                  if (!dateRegex.test(v)) { setDateStatus('invalid'); return; }
                  const parsed = new Date(v);
                  if (isNaN(parsed.getTime())) { setDateStatus('invalid'); return; }
                  setDateStatus(parsed > new Date() ? 'valid' : 'past');
                }}
                keyboardType="numbers-and-punctuation"
              />
              {dateStatus === 'invalid' && (
                <Text style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>Use format: YYYY-MM-DD (e.g. 2026-09-20)</Text>
              )}
              {dateStatus === 'past' && (
                <Text style={{ color: '#f97316', fontSize: 11, marginTop: 4 }}>⚠️ This date has already passed</Text>
              )}
              {dateStatus === 'valid' && (
                <Text style={{ color: '#22c55e', fontSize: 11, marginTop: 4 }}>✓ Valid date</Text>
              )}
              {dateStatus === 'idle' && (
                <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 4 }}>Format: YYYY-MM-DD (year-month-day)</Text>
              )}
            </View>

            <View style={styles.fieldGroup}>
              <Text style={[styles.fieldLabel, { color: c.textSecondary }]}>Weight Class</Text>
              <View style={styles.chipRow}>
                {WEIGHT_CLASSES.map((kg) => (
                  <TouchableOpacity
                    key={kg}
                    style={[
                      styles.chip,
                      {
                        backgroundColor: weightClass === kg ? c.accent : c.surfaceVariant,
                        borderColor: weightClass === kg ? c.accent : c.border,
                      },
                    ]}
                    onPress={() => { setWeightClass(kg); persistMeetSetup({ weightClass: kg }); }}>
                    <Text
                      style={[
                        styles.chipText,
                        { color: weightClass === kg ? c.accentForeground : c.textSecondary },
                      ]}>
                      {weightClassLabel(kg)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.fieldGroup}>
              <Text style={[styles.fieldLabel, { color: c.textSecondary }]}>Federation</Text>
              <View style={styles.chipRow}>
                {FEDERATIONS.map((fed) => (
                  <TouchableOpacity
                    key={fed}
                    style={[
                      styles.chip,
                      {
                        backgroundColor: federation === fed ? c.accent : c.surfaceVariant,
                        borderColor: federation === fed ? c.accent : c.border,
                      },
                    ]}
                    onPress={() => { setFederation(fed); persistMeetSetup({ federation: fed }); }}>
                    <Text
                      style={[
                        styles.chipText,
                        { color: federation === fed ? c.accentForeground : c.textSecondary },
                      ]}>
                      {fed}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity
              style={[styles.saveButton, { backgroundColor: c.accent }]}
              onPress={handleSave}
              disabled={saving}>
              {saving ? (
                <ActivityIndicator color={c.accentForeground} />
              ) : (
                <Text style={[styles.saveButtonText, { color: c.accentForeground }]}>Save Meet</Text>
              )}
            </TouchableOpacity>
          </View>

          {/* ── Section 2: Attempt Selection ── */}
          <View style={[styles.section, { backgroundColor: c.surface }]}>
            <View style={styles.sectionHeaderRow}>
              <Scale size={16} color={c.accent} />
              <Text style={[styles.sectionTitle, { color: c.text }]}>ATTEMPT SELECTION</Text>
              {liftsLoading && <ActivityIndicator size="small" color={c.accent} />}
            </View>

            {bestLifts && (
              <Text style={[styles.e1rmNote, { color: c.textTertiary }]}>
                Based on e1RM — Squat {Math.round(bestLifts.squat)}, Bench {Math.round(bestLifts.bench)}, Deadlift {Math.round(bestLifts.deadlift)} lbs
              </Text>
            )}

            {/* Attempt grid */}
            <View style={styles.attemptGrid}>
              {/* Header row */}
              <View style={styles.attemptRow}>
                <Text style={[styles.attemptRowLabel, { color: c.textTertiary }]} />
                {(['Squat', 'Bench', 'Deadlift'] as const).map((lift) => (
                  <Text key={lift} style={[styles.attemptColHeader, { color: c.accent }]}>
                    {lift}
                  </Text>
                ))}
              </View>

              {/* Data rows */}
              {(['opener', 'second', 'third'] as const).map((field, fieldIdx) => {
                const rowLabels = { opener: 'Opener', second: '2nd', third: '3rd' };
                return (
                  <View
                    key={field}
                    style={[
                      styles.attemptRow,
                      fieldIdx < 2 && { borderBottomWidth: 1, borderBottomColor: c.border },
                    ]}>
                    <Text style={[styles.attemptRowLabel, { color: c.textSecondary }]}>
                      {rowLabels[field]}
                    </Text>
                    {(['squat', 'bench', 'deadlift'] as LiftKey[]).map((lift) => (
                      <TextInput
                        key={lift}
                        style={[
                          styles.attemptInput,
                          {
                            backgroundColor: c.surfaceVariant,
                            borderColor: c.border,
                            color: c.text,
                          },
                        ]}
                        value={attempts[lift][field]}
                        onChangeText={(v) => updateAttempt(lift, field, v)}
                        keyboardType="numeric"
                        placeholder="—"
                        placeholderTextColor={c.textTertiary}
                      />
                    ))}
                  </View>
                );
              })}
            </View>

            {/* Projected total */}
            {(Number(attempts.squat.third) || Number(attempts.bench.third) || Number(attempts.deadlift.third)) ? (
              <View style={[styles.totalRow, { borderTopColor: c.border }]}>
                <Text style={[styles.totalLabel, { color: c.textSecondary }]}>Projected Total</Text>
                <Text style={[styles.totalValue, { color: c.accent }]}>
                  {total.total} lbs / {total.totalKg} kg
                </Text>
              </View>
            ) : null}
          </View>

          {/* ── Section 3: Weight Cut Status ── */}
          <View style={[styles.section, { backgroundColor: c.surface }]}>
            <View style={styles.sectionHeaderRow}>
              <Scale size={16} color={c.accent} />
              <Text style={[styles.sectionTitle, { color: c.text }]}>WEIGHT CUT STATUS</Text>
            </View>

            {cutStatus ? (
              <>
                <View style={styles.weightRow}>
                  <View style={styles.weightItem}>
                    <Text style={[styles.weightItemLabel, { color: c.textTertiary }]}>Current</Text>
                    <Text style={[styles.weightItemValue, { color: c.text }]}>
                      {cutStatus.currentKg} kg
                    </Text>
                  </View>
                  <View style={styles.weightArrow}>
                    <Text style={{ color: c.textTertiary, fontSize: 18 }}>→</Text>
                  </View>
                  <View style={styles.weightItem}>
                    <Text style={[styles.weightItemLabel, { color: c.textTertiary }]}>Target</Text>
                    <Text style={[styles.weightItemValue, { color: c.accent }]}>
                      {weightClassLabel(weightClass)}
                    </Text>
                  </View>
                </View>

                <View
                  style={[
                    styles.cutStatusBadge,
                    {
                      backgroundColor: cutStatus.onTrack
                        ? '#4ade8022'
                        : '#f8717122',
                      borderColor: cutStatus.onTrack ? '#4ade80' : '#f87171',
                    },
                  ]}>
                  <Text
                    style={{
                      color: cutStatus.onTrack ? '#4ade80' : '#f87171',
                      fontFamily: TYPO.body.semibold,
                      fontSize: 14,
                    }}>
                    {cutStatus.onTrack
                      ? "You're on track ✅"
                      : `You need to cut ${cutStatus.deltaKg} kg`}
                  </Text>
                </View>
              </>
            ) : (
              <Text style={[styles.emptyNote, { color: c.textTertiary }]}>
                Add bodyweight to your profile to see weight cut status.
              </Text>
            )}
          </View>

          {/* ── Section 4: Meet Day Checklist ── */}
          <View style={[styles.section, { backgroundColor: c.surface }]}>
            <View style={styles.sectionHeaderRow}>
              <ClipboardList size={16} color={c.accent} />
              <Text style={[styles.sectionTitle, { color: c.text }]}>MEET DAY CHECKLIST</Text>
            </View>

            {Object.entries(categorized).map(([category, items]) => (
              <View key={category} style={{ marginBottom: 8 }}>
                <Text style={[styles.checklistCategory, { color: c.accent }]}>
                  {category.toUpperCase()}
                </Text>
                {items.map((item, idx) => (
                  <TouchableOpacity
                    key={item.id}
                    style={[
                      styles.checklistRow,
                      {
                        borderBottomColor: c.border,
                        borderBottomWidth: idx < items.length - 1 ? 1 : 0,
                      },
                    ]}
                    onPress={() => toggleCheckItem(item.id)}>
                    <View
                      style={[
                        styles.checkbox,
                        {
                          backgroundColor: checkedItems[item.id] ? c.accent : 'transparent',
                          borderColor: checkedItems[item.id] ? c.accent : c.border,
                        },
                      ]}>
                      {checkedItems[item.id] && (
                        <Text style={{ color: c.accentForeground, fontSize: 10, fontWeight: '800' }}>✓</Text>
                      )}
                    </View>
                    <Text
                      style={[
                        styles.checklistLabel,
                        {
                          color: checkedItems[item.id] ? c.textTertiary : c.text,
                          textDecorationLine: checkedItems[item.id] ? 'line-through' : 'none',
                        },
                      ]}>
                      {item.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            ))}
          </View>

          {/* ── Section 5: Countdown ── */}
          {meetDate ? (
            <View style={[styles.section, { backgroundColor: c.surface, alignItems: 'center' }]}>
              <View style={styles.sectionHeaderRow}>
                <Timer size={16} color={c.accent} />
                <Text style={[styles.sectionTitle, { color: c.text }]}>COUNTDOWN</Text>
              </View>
              <Text style={[styles.countdownNumber, { color: c.accent }]}>
                {days ?? 0}
              </Text>
              <Text style={[styles.countdownLabel, { color: c.textSecondary }]}>
                days until{meetName ? ` ${meetName}` : ' your meet'}
              </Text>
              {days === 0 && (
                <Text style={[styles.countdownSub, { color: '#4ade80' }]}>
                  🏆 IT'S MEET DAY. GO GET IT.
                </Text>
              )}
            </View>
          ) : (
            <View style={[styles.section, { backgroundColor: c.surface, alignItems: 'center' }]}>
              <Timer size={32} color={c.textTertiary} />
              <Text style={[styles.emptyNote, { color: c.textTertiary, textAlign: 'center', marginTop: 8 }]}>
                Set a meet date to see the countdown.
              </Text>
            </View>
          )}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    gap: 8,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 48,
    gap: 16,
  },
  section: {
    borderRadius: 2,
    padding: 16,
    gap: 14,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 0.8,
  },
  fieldGroup: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontFamily: TYPO.body.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    fontFamily: TYPO.body.regular,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  chipText: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
  saveButton: {
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
    marginTop: 4,
  },
  saveButtonText: {
    fontSize: 15,
    fontFamily: TYPO.heading.bold,
  },
  e1rmNote: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
  },
  attemptGrid: {
    gap: 0,
  },
  attemptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 8,
  },
  attemptRowLabel: {
    width: 52,
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
  attemptColHeader: {
    flex: 1,
    fontSize: 12,
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
    letterSpacing: 0.5,
  },
  attemptInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 8,
    paddingHorizontal: 6,
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    paddingTop: 12,
  },
  totalLabel: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
  totalValue: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
  },
  weightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
  },
  weightItem: {
    alignItems: 'center',
    gap: 4,
  },
  weightItemLabel: {
    fontSize: 11,
    fontFamily: TYPO.body.regular,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  weightItemValue: {
    fontSize: 22,
    fontFamily: TYPO.heading.bold,
  },
  weightArrow: {
    alignItems: 'center',
  },
  cutStatusBadge: {
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  emptyNote: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
  },
  checklistCategory: {
    fontSize: 11,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
    marginBottom: 8,
    marginTop: 4,
  },
  checklistRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1.5,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checklistLabel: {
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    flex: 1,
  },
  countdownNumber: {
    fontSize: 72,
    fontFamily: TYPO.heading.bold,
    lineHeight: 80,
  },
  countdownLabel: {
    fontSize: 15,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
  },
  countdownSub: {
    fontSize: 13,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
    marginTop: 4,
  },
});
